import { Injectable } from '@angular/core';
import { CoveoService } from '../coveo/coveo.service';
import { Unsubscribe } from '@coveo/headless';
import {
  InstantProducts,
  Product,
  InteractiveProduct,
  InstantProductsProps,
} from '@coveo/headless/commerce';
import { BehaviorSubject, filter, Observable, Subscription, take } from 'rxjs';
import { TypeaheadProduct } from '@shared/services/typeahead/models/typeahead.model';
import { highlightText } from '@shared/services/typeahead/utils/typeahead.util';

@Injectable({
  providedIn: 'root',
})
export class RecipeProductsTypeaheadService {
  constructor(private coveoService: CoveoService) {}

  /**
   * Controller instance for managing instant products search functionality.
   * Used internally to handle product search and selection.
   */
  instantProductsController: InstantProducts;

  private mppSearchBoxId = 'mpp-search-box';
  instantProductsUnsubscribe: Unsubscribe;
  commerceEngineSubscription$: Subscription;
  private productsTypeaheadSubject = new BehaviorSubject<TypeaheadProduct[]>(
    [],
  );

  /**
   * Observable that emits a list of typeahead products.
   * Subscribe to this to receive updates when products are searched or selected.
   * The products are limited to the maxProductsToDisplay value set during initialization.
   * Each product includes highlighted text for matching search terms.
   */
  products$: Observable<TypeaheadProduct[]> =
    this.productsTypeaheadSubject.asObservable();

  /**
   * Initializes the typeahead functionality for recipe products.
   * This method should be called when setting up the typeahead component.
   * It sets up the necessary controllers and subscriptions for product search.
   *
   * @param maxProductsToDisplay - Maximum number of products to show in the typeahead results.
   *                              Defaults to 5 if not specified.
   */
  initializeRecipeProductsTypeAhead(maxProductsToDisplay: number = 5) {
    this.commerceEngineSubscription$ = this.coveoService
      .getCommerceEngine$()
      .pipe(
        take(1),
        filter(engine => !!engine),
      )
      .subscribe(engine => {
        const props: InstantProductsProps = {
          options: {
            searchBoxId: this.mppSearchBoxId,
          },
        };

        this.instantProductsController = this.coveoService.buildInstantProducts(
          engine,
          props,
        );

        this.instantProductsUnsubscribe =
          this.instantProductsController.subscribe(() => {
            const newProducts: TypeaheadProduct[] = [];
            const products: Product[] =
              this.instantProductsController.state.products;
            if (products?.length) {
              products.slice(0, maxProductsToDisplay).map(prod =>
                newProducts.push({
                  ...prod,
                  productName: highlightText(prod.ec_name, prod.nameHighlights),
                }),
              );
            }
            this.productsTypeaheadSubject.next(newProducts);
          });
      });
  }

  /**
   * Handles the selection of a product from the typeahead results.
   * This method should be called when a user clicks on a product in the typeahead dropdown.
   * It creates an interactive product instance and triggers the selection process.
   *
   * @param product - The product that was selected by the user
   */
  selectInstantProduct(product: Product) {
    product.ec_price = product.ec_price ?? 0;
    const interactiveProduct: InteractiveProduct =
      this.instantProductsController.interactiveProduct({
        options: {
          product: product,
        },
      });
    interactiveProduct.select();
  }

  /**
   * Updates the search query when the user types in the search box.
   * This method should be called whenever the search input value changes.
   *
   * @param inputValue - The current value of the search input
   */
  handleInputChange(inputValue: string) {
    if (inputValue === '') {
      this.productsTypeaheadSubject.next([]);
    } else {
      this.instantProductsController.updateQuery(inputValue);
    }
  }

  /**
   * Disables the typeahead functionality and cleans up resources.
   * This method should be called when the component is destroyed or when
   * the typeahead needs to be disabled. It unsubscribes from all active
   * subscriptions and resets the products state.
   */
  disableTypeahead() {
    this.commerceEngineSubscription$?.unsubscribe();

    if (!!this.instantProductsUnsubscribe) {
      this.instantProductsUnsubscribe();
    }
    this.productsTypeaheadSubject.next([]);
  }
}
